/**
 * Debug Utilities
 * Enhanced debugging tools for development
 */

// ============================================================================
// ENVIRONMENT CHECKS
// ============================================================================

/**
 * Check if we're in development mode
 */
export const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Check if debug mode is enabled
 */
export const isDebugEnabled = process.env.NEXT_PUBLIC_ENABLE_DEBUG === 'true';

/**
 * Check if we're running on the server
 */
export const isServer = typeof window === 'undefined';

/**
 * Check if we're running in the browser
 */
export const isClient = !isServer;

// ============================================================================
// PERFORMANCE DEBUGGING
// ============================================================================

/**
 * Measure the execution time of a function
 *
 * @example
 * const result = await measureTime('fetchUsers', async () => {
 *   return await api.fetchUsers();
 * });
 * // Logs: "[fetchUsers] completed in 234.56ms"
 */
export async function measureTime<T>(
  label: string,
  fn: () => T | Promise<T>
): Promise<T> {
  if (!isDebugEnabled) {
    return fn();
  }

  const start = performance.now();

  try {
    const result = await fn();
    const duration = performance.now() - start;

    console.log(
      `%c[${label}] completed in ${duration.toFixed(2)}ms`,
      'color: #10b981; font-weight: bold;'
    );

    return result;
  } catch (error) {
    const duration = performance.now() - start;

    console.error(
      `%c[${label}] failed after ${duration.toFixed(2)}ms`,
      'color: #ef4444; font-weight: bold;',
      error
    );

    throw error;
  }
}

/**
 * Create a performance tracker for multiple operations
 */
export class PerformanceTracker {
  private marks: Map<string, number> = new Map();
  private measures: Array<{ name: string; duration: number }> = [];

  mark(name: string): void {
    if (!isDebugEnabled) return;
    this.marks.set(name, performance.now());
  }

  measure(name: string, startMark: string, endMark?: string): void {
    if (!isDebugEnabled) return;

    const start = this.marks.get(startMark);
    const end = endMark ? this.marks.get(endMark) : performance.now();

    if (start && end) {
      const duration = end - start;
      this.measures.push({ name, duration });

      console.log(
        `%c[Performance] ${name}: ${duration.toFixed(2)}ms`,
        'color: #3b82f6; font-weight: bold;'
      );
    }
  }

  summary(): void {
    if (!isDebugEnabled || this.measures.length === 0) return;

    console.group('Performance Summary');
    console.table(this.measures);

    const total = this.measures.reduce((sum, m) => sum + m.duration, 0);
    console.log(`Total: ${total.toFixed(2)}ms`);
    console.groupEnd();
  }

  clear(): void {
    this.marks.clear();
    this.measures = [];
  }
}

// ============================================================================
// API DEBUGGING
// ============================================================================

/**
 * Debug wrapper for API calls
 *
 * @example
 * const data = await debugApi('fetchUser', () => api.getUser(id));
 */
export async function debugApi<T>(
  endpoint: string,
  apiCall: () => Promise<T>
): Promise<T> {
  if (!isDebugEnabled) {
    return apiCall();
  }

  console.group(`[API] ${endpoint}`);
  console.time(endpoint);

  try {
    const result = await apiCall();
    console.timeEnd(endpoint);
    console.log('Response:', result);
    console.groupEnd();
    return result;
  } catch (error) {
    console.timeEnd(endpoint);
    console.error('Error:', error);
    console.groupEnd();
    throw error;
  }
}

// ============================================================================
// ERROR DEBUGGING
// ============================================================================

/**
 * Enhanced error with additional context
 */
export class DebugError extends Error {
  public readonly context: Record<string, unknown>;
  public readonly timestamp: string;
  public readonly component?: string;

  constructor(
    message: string,
    context: Record<string, unknown> = {},
    component?: string
  ) {
    super(message);
    this.name = 'DebugError';
    this.context = context;
    this.timestamp = new Date().toISOString();
    this.component = component;

    if (isDebugEnabled) {
      console.group(`[DebugError] ${message}`);
      console.log('Timestamp:', this.timestamp);
      console.log('Component:', component || 'Unknown');
      console.log('Context:', context);
      console.trace('Stack trace:');
      console.groupEnd();
    }
  }
}

// ============================================================================
// LOGGING UTILITIES
// ============================================================================

/**
 * Conditional logging based on debug mode
 */
export const debug = {
  log: (...args: unknown[]) => {
    if (isDebugEnabled) {
      console.log('[DEBUG]', ...args);
    }
  },

  info: (...args: unknown[]) => {
    if (isDebugEnabled) {
      console.info('[INFO]', ...args);
    }
  },

  warn: (...args: unknown[]) => {
    if (isDebugEnabled) {
      console.warn('[WARN]', ...args);
    }
  },

  error: (...args: unknown[]) => {
    if (isDebugEnabled) {
      console.error('[ERROR]', ...args);
    }
  },

  group: (label: string) => {
    if (isDebugEnabled) {
      console.group(`[DEBUG] ${label}`);
    }
  },

  groupEnd: () => {
    if (isDebugEnabled) {
      console.groupEnd();
    }
  },

  table: (data: unknown) => {
    if (isDebugEnabled) {
      console.table(data);
    }
  },

  time: (label: string) => {
    if (isDebugEnabled) {
      console.time(`[TIMER] ${label}`);
    }
  },

  timeEnd: (label: string) => {
    if (isDebugEnabled) {
      console.timeEnd(`[TIMER] ${label}`);
    }
  }
};

// ============================================================================
// DEVELOPMENT HELPERS
// ============================================================================

/**
 * Assert a condition in development
 */
export function assert(
  condition: unknown,
  message: string,
  context?: Record<string, unknown>
): asserts condition {
  if (!condition && isDevelopment) {
    const error = new DebugError(`Assertion failed: ${message}`, context);
    throw error;
  }
}

/**
 * Mark a function as deprecated
 */
export function deprecated(
  message: string,
  alternative?: string
): MethodDecorator {
  return function (
    _target: unknown,
    propertyKey: string | symbol,
    descriptor: PropertyDescriptor
  ) {
    const original = descriptor.value as (...args: unknown[]) => unknown;

    descriptor.value = function (this: unknown, ...args: unknown[]) {
      if (isDevelopment) {
        console.warn(
          `%c[DEPRECATED] ${String(propertyKey)}: ${message}`,
          'color: #f59e0b; font-weight: bold;'
        );

        if (alternative) {
          console.warn(`Use ${alternative} instead.`);
        }
      }

      return original.apply(this, args);
    };

    return descriptor;
  };
}

/**
 * Inspect an object in development
 */
export function inspect(label: string, obj: unknown): void {
  if (!isDebugEnabled) return;

  console.group(`[INSPECT] ${label}`);
  console.log('Type:', typeof obj);
  console.log('Value:', obj);

  if (obj && typeof obj === 'object') {
    console.log('Keys:', Object.keys(obj));
    console.log(
      'Constructor:',
      (obj as { constructor?: { name?: string } }).constructor?.name
    );

    if (Array.isArray(obj)) {
      console.log('Length:', obj.length);
    }
  }

  console.groupEnd();
}
